import React, { useState } from "react";
import { getFieldDisplayName } from "../utils/fieldMapping";
import { exportToCSV, exportToExcel, exportToJSON, exportDetailedCSV } from "../utils/reportExport";

const ReportPreview = ({ data, templateConfig, fieldMetadata }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [viewMode, setViewMode] = useState('table'); // table, summary, raw
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Переключение развернутого состояния группы
  const toggleGroup = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  // Развернуть/свернуть все группы
  const toggleAllGroups = () => {
    if (expandedGroups.size === data.length) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(data.map((item, index) => item.groupKey || index)));
    }
  };

  // Получение названия поля
  const getFieldLabel = (fieldKey) => {
    return getFieldDisplayName(fieldKey);
  };

  // Функция для замены технических названий полей в groupKey на человекочитаемые
  const formatGroupKey = (groupKey) => {
    if (!groupKey || typeof groupKey !== 'string') return groupKey;

    // Ищем паттерны вида "fieldId: value" и заменяем fieldId на человекочитаемое название
    // Обрабатываем как простые fieldId, так и сложные ufCrm3_... поля
    return groupKey.replace(/([a-zA-Z_][a-zA-Z0-9_]*?):/g, (match, fieldId) => {
      const displayName = getFieldDisplayName(fieldId);
      return `${displayName}:`;
    });
  };

  // Форматирование значения
  const formatValue = (value, fieldKey) => {
    if (value === null || value === undefined) return 'N/A';

    // Специальное форматирование для временных метрик
    if (fieldKey && (fieldKey.startsWith('timeline_') || fieldKey.startsWith('_time_') || fieldKey === '_totalProductionTime')) {
      return formatTimeValue(value);
    }

    if (typeof value === 'number') {
      return value.toLocaleString('ru-RU');
    }

    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }

    return String(value);
  };

  // Форматирование временных значений
  const formatTimeValue = (hours) => {
    if (typeof hours !== 'number' || isNaN(hours)) return 'N/A';

    const totalHours = parseFloat(hours);

    if (totalHours < 1) {
      // Меньше часа - показываем в минутах
      return `${Math.round(totalHours * 60)} мин`;
    } else if (totalHours < 24) {
      // Меньше суток - показываем часы и минуты
      const h = Math.floor(totalHours);
      const m = Math.round((totalHours - h) * 60);
      return m > 0 ? `${h}ч ${m}м` : `${h}ч`;
    } else {
      // Больше суток - показываем дни и часы
      const days = Math.floor(totalHours / 24);
      const remainingHours = Math.round(totalHours % 24);
      return remainingHours > 0 ? `${days}д ${remainingHours}ч` : `${days}д`;
    }
  };

  // Получение ключевых полей для отображения
  const getDisplayFields = () => {
    const fields = new Set();

    // Добавляем поля из группировок
    templateConfig.grouping?.forEach(group => {
      if (group.field && typeof group.field === 'string') {
        fields.add(group.field);
      }
    });

    // Добавляем поля из агрегаций
    templateConfig.aggregations?.forEach(agg => {
      if (agg.outputField && typeof agg.outputField === 'string') {
        fields.add(agg.outputField);
      }
    });

    // Добавляем несколько основных полей из данных
    if (data.length > 0) {
      const sampleItem = data[0];
      // Проверяем и в items, и в самом объекте
      const itemToCheck = sampleItem.items?.[0] || sampleItem;
      const itemKeys = Object.keys(itemToCheck);

      // Основные поля
      ['id', 'title', 'orderNumber', 'createdTime', 'opportunity'].forEach(key => {
        if (itemKeys.includes(key)) {
          fields.add(key);
        }
      });

      // НЕ добавляем автоматические временные поля (_time_* и _totalProductionTime)
      // Они используются только для расчета итогов, но не отображаются как отдельные колонки
    }

    const result = Array.from(fields).filter(field => typeof field === 'string');

    console.log('🎯 ReportPreview - Поля для отображения:', {
      allFields: result,
      timelineFields: result.filter(f => f.startsWith('timeline_') || f.startsWith('_time_')),
      sampleData: data[0],
      sampleItem: data[0]?.items?.[0] || data[0]
    });

    return result;
  };

  // Вычисление общих итогов
  const calculateTotals = () => {
    const totals = {};

    templateConfig.aggregations?.forEach(agg => {
      const values = data.map(item => item[agg.outputField]).filter(v => v !== null && v !== undefined);

      switch (agg.type) {
        case 'count':
          totals[agg.outputField] = values.reduce((sum, val) => sum + val, 0);
          break;
        case 'sum':
          totals[agg.outputField] = values.reduce((sum, val) => sum + val, 0);
          break;
        case 'avg':
          totals[agg.outputField] = values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0;
          break;
        case 'min':
          totals[agg.outputField] = values.length > 0 ? Math.min(...values) : 0;
          break;
        case 'max':
          totals[agg.outputField] = values.length > 0 ? Math.max(...values) : 0;
          break;

        // Временные агрегации - для итогов используем среднее значение
        case 'timeline_avg_total':
        case 'timeline_avg_stage':
          totals[agg.outputField] = values.length > 0 ? values.reduce((sum, val) => sum + val, 0) / values.length : 0;
          break;
        case 'timeline_min_total':
        case 'timeline_min_stage':
          totals[agg.outputField] = values.length > 0 ? Math.min(...values) : 0;
          break;
        case 'timeline_max_total':
        case 'timeline_max_stage':
          totals[agg.outputField] = values.length > 0 ? Math.max(...values) : 0;
          break;
      }
    });

    // Вычисляем средние для индивидуальных временных полей (_time_*)
    if (data.length > 0 && data[0].items && data[0].items.length > 0) {
      // Если данные сгруппированы, берем все items из всех групп
      const allItems = data.flatMap(group => group.items || []);

      // Находим все временные поля
      const timeFields = Object.keys(allItems[0] || {}).filter(key =>
        key.startsWith('_time_') || key === '_totalProductionTime'
      );

      timeFields.forEach(field => {
        const values = allItems
          .map(item => item[field])
          .filter(v => typeof v === 'number' && !isNaN(v));

        if (values.length > 0) {
          totals[field] = values.reduce((sum, val) => sum + val, 0) / values.length;
        }
      });
    }

    return totals;
  };

  if (!data || data.length === 0) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Превью отчета</h3>
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Данные не найдены</h3>
          <p className="text-gray-500">
            Настройте фильтры и параметры шаблона для генерации отчета
          </p>
        </div>
      </div>
    );
  }

  const displayFields = getDisplayFields();
  const totals = calculateTotals();
  const isGrouped = templateConfig.grouping && templateConfig.grouping.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Превью отчета</h3>

        <div className="flex items-center space-x-4">
          {/* Кнопка экспорта */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Экспорт</span>
            </button>

            {/* Меню экспорта */}
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                <div className="py-1">
                  <button
                    onClick={() => {
                      exportToCSV(data, templateConfig, `preview_${templateConfig.name || 'report'}`);
                      setShowExportMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    📄 CSV (Сводка)
                  </button>
                  <button
                    onClick={() => {
                      exportToExcel(data, templateConfig, `preview_${templateConfig.name || 'report'}`);
                      setShowExportMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    📊 Excel
                  </button>
                  <button
                    onClick={() => {
                      exportDetailedCSV(data, templateConfig, `preview_detailed_${templateConfig.name || 'report'}`);
                      setShowExportMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    📋 CSV (Детальный)
                  </button>
                  <button
                    onClick={() => {
                      exportToJSON(data, templateConfig, `preview_${templateConfig.name || 'report'}`);
                      setShowExportMenu(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    💾 JSON
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Переключатель режимов */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('summary')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'summary'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Сводка
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'table'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Таблица
            </button>
            <button
              onClick={() => setViewMode('raw')}
              className={`px-3 py-1 text-sm rounded ${
                viewMode === 'raw'
                  ? 'bg-blue-100 text-blue-800'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              JSON
            </button>
          </div>

          {/* Управление группами */}
          {isGrouped && viewMode === 'table' && (
            <button
              onClick={toggleAllGroups}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
            >
              {expandedGroups.size === data.length ? 'Свернуть все' : 'Развернуть все'}
            </button>
          )}
        </div>
      </div>

      {/* Сводная информация */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{data.length}</div>
            <div className="text-sm text-blue-700">
              {isGrouped ? 'Групп' : 'Записей'}
            </div>
          </div>
          {templateConfig.filters && templateConfig.filters.length > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{templateConfig.filters.length}</div>
              <div className="text-sm text-green-700">Фильтров</div>
            </div>
          )}
          {templateConfig.grouping && templateConfig.grouping.length > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{templateConfig.grouping.length}</div>
              <div className="text-sm text-purple-700">Группировок</div>
            </div>
          )}
          {templateConfig.aggregations && templateConfig.aggregations.length > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{templateConfig.aggregations.length}</div>
              <div className="text-sm text-orange-700">Счетчиков</div>
            </div>
          )}
        </div>
      </div>

      {/* Режим сводки */}
      {viewMode === 'summary' && (
        <div className="space-y-4">
          {/* Итоги по агрегациям */}
          {Object.keys(totals).length > 0 && (
            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Общие итоги</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Object.entries(totals).map(([field, value]) => {
                  const agg = templateConfig.aggregations?.find(a => a.outputField === field);
                  const isTimeline = field.startsWith('timeline_');
                  return (
                    <div
                      key={field}
                      className={`text-center p-3 rounded ${
                        isTimeline
                          ? 'bg-purple-50 border border-purple-200'
                          : 'bg-gray-50'
                      }`}
                    >
                      <div className={`text-xl font-bold ${
                        isTimeline ? 'text-purple-700' : 'text-gray-900'
                      }`}>
                        {formatValue(value, field)}
                      </div>
                      <div className={`text-sm ${
                        isTimeline ? 'text-purple-600' : 'text-gray-600'
                      }`}>
                        {isTimeline && '⏱️ '}
                        {agg?.label || field}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Топ-5 групп */}
          {isGrouped && (
            <div className="bg-white border rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Топ групп</h4>
              <div className="space-y-2">
                {data.slice(0, 5).map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="font-medium text-gray-900">
                      {formatGroupKey(item.groupKey) || `Группа ${index + 1}`}
                    </div>
                    <div className="text-gray-600">
                      {item.count || item.items?.length || 0} записей
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Режим таблицы */}
      {viewMode === 'table' && (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {isGrouped && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Группа
                    </th>
                  )}
                  {displayFields.map((field) => (
                    <th key={field} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      {getFieldLabel(field)}
                    </th>
                  ))}
                  {isGrouped && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Действия
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((item, index) => (
                  <React.Fragment key={index}>
                    <tr className={isGrouped ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                      {isGrouped && (
                        <td className="px-4 py-3 font-medium text-gray-900">
                          {formatGroupKey(item.groupKey) || `Группа ${index + 1}`}
                        </td>
                      )}
                      {displayFields.map((field) => {
                        const isTimeline = field.startsWith('timeline_') || field.startsWith('_time_') || field === '_totalProductionTime';
                        return (
                          <td
                            key={field}
                            className={`px-4 py-3 text-sm ${
                              isTimeline
                                ? 'font-medium text-purple-700 bg-purple-50'
                                : 'text-gray-600'
                            }`}
                          >
                            {isTimeline && '⏱️ '}
                            {formatValue(item[field], field)}
                          </td>
                        );
                      })}
                      {isGrouped && (
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleGroup(item.groupKey || index)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            {expandedGroups.has(item.groupKey || index) ? 'Скрыть' : 'Показать'} детали
                          </button>
                        </td>
                      )}
                    </tr>

                    {/* Детали группы */}
                    {isGrouped && expandedGroups.has(item.groupKey || index) && item.items && (
                      item.items.slice(0, 10).map((subItem, subIndex) => (
                        <tr key={`${index}-${subIndex}`} className="bg-gray-50">
                          <td className="px-8 py-2 text-xs text-gray-500">
                            #{subIndex + 1}
                          </td>
                          {displayFields.map((field) => (
                            <td key={field} className="px-4 py-2 text-xs text-gray-600">
                              {formatValue(subItem[field], field)}
                            </td>
                          ))}
                          <td className="px-4 py-2"></td>
                        </tr>
                      ))
                    )}

                    {/* Показать больше записей */}
                    {isGrouped && expandedGroups.has(item.groupKey || index) && item.items && item.items.length > 10 && (
                      <tr className="bg-gray-50">
                        <td colSpan={displayFields.length + 2} className="px-4 py-2 text-center text-xs text-gray-500">
                          ... и еще {item.items.length - 10} записей
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>

              {/* Итоговая строка */}
              {Object.keys(totals).length > 0 && (
                <tfoot className="bg-gray-100">
                  <tr>
                    {isGrouped && (
                      <td className="px-4 py-3 font-bold text-gray-900">
                        ИТОГО
                      </td>
                    )}
                    {displayFields.map((field) => {
                      const isTimeline = field.startsWith('timeline_') || field.startsWith('_time_') || field === '_totalProductionTime';
                      return (
                        <td
                          key={field}
                          className={`px-4 py-3 font-bold ${
                            isTimeline ? 'text-purple-700 bg-purple-50' : 'text-gray-900'
                          }`}
                        >
                          {totals[field] !== undefined && totals[field] !== null ? (
                            <>
                              {isTimeline && '⏱️ '}
                              {formatValue(totals[field], field)}
                            </>
                          ) : (
                            ''
                          )}
                        </td>
                      );
                    })}
                    {isGrouped && <td className="px-4 py-3"></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Режим JSON */}
      {viewMode === 'raw' && (
        <div className="bg-white border rounded-lg p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-gray-900">JSON данные</h4>
            <button
              onClick={() => navigator.clipboard.writeText(JSON.stringify(data, null, 2))}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
            >
              Копировать
            </button>
          </div>
          <pre className="bg-gray-50 p-4 rounded overflow-auto max-h-96 text-xs">
            {JSON.stringify(data.slice(0, 5), null, 2)}
          </pre>
          {data.length > 5 && (
            <div className="text-center text-sm text-gray-500 mt-2">
              Показаны первые 5 записей из {data.length}
            </div>
          )}
        </div>
      )}

      {/* Информация о конфигурации */}
      <div className="bg-gray-50 border rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Конфигурация шаблона</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <div><strong>Фильтров:</strong> {templateConfig.filters?.length || 0}</div>
          <div><strong>Группировок:</strong> {templateConfig.grouping?.length || 0}</div>
          <div><strong>Счетчиков:</strong> {templateConfig.aggregations?.length || 0}</div>
          {templateConfig.dateRange?.from && (
            <div><strong>Период:</strong> {templateConfig.dateRange.from} - {templateConfig.dateRange.to || 'сейчас'}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;